import {
	VIEW_MODES,
	type ViewMode,
} from '@atlassian/jira-portfolio-3-common/src/common/types/view-mode.tsx';
import { isConfluenceMacroProxyForIdeas } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/app/index.tsx';
import { createSelector } from '@atlassian/jira-portfolio-3-portfolio/src/common/reselect/index.tsx';
import type {
	ColumnConfig,
	RowConfig,
} from '@atlassian/jira-portfolio-3-treegrid/src/common/types.tsx';
import { isFocusConnectedWithReadPermissions } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/system/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import type { CustomField } from '../../state/domain/custom-fields/types.tsx';
import {
	type FieldColumnsState,
	isColumnId,
} from '../../state/domain/view-settings/field-columns/types.tsx';
import { getSupportedCustomFields } from '../custom-fields/index.tsx';
import { isAtlasConnectInstalled } from '../system/index.tsx';
import {
	type TableItem,
	getTableItems,
	getScrollToIndex,
	TABLE_GROUP,
	TABLE_GROUP_HEADER,
	TABLE_ISSUE,
	TABLE_BLANK_ROW,
	TABLE_INLINE_CREATE,
} from '../table/index.tsx';
import { getFieldColumnsViewSettingsByViewMode, getViewMode } from '../view-settings/index.tsx';
import { isReleasesEnabled } from '../plan/index.tsx';
import {
	HEADER,
	SCOPE,
	SUBHEADER,
	TABLE_ITEM,
	ADD_FIELDS,
	FIELD,
	TIMELINE,
	EMPTY,
	TIMELINE_HEADERS_ROWS,
} from './constants.tsx';

import type { GridRow, GridColumn } from './types.tsx';
import { getGridRowId } from './utils.tsx';

export const getTableItemTagsPure = (tableItems: TableItem[]) => tableItems.map(({ tag }) => tag);
/**
 * Returns an array of tags for the table items.
 *
 * This unlocks the performance onf the getGridRows() (re-calculated on the changes on this string[] only)
 */
export const getTableItemTags = createSelector([getTableItems], getTableItemTagsPure);

export const getGridRowIdsPure = (tableItems: TableItem[]) => [
	HEADER,
	SUBHEADER,
	...tableItems.map(getGridRowId),
];

/** Returns an array of unique ids for the table items. */
export const getGridRowIds = createSelector([getTableItems], getGridRowIdsPure);

export const getGridRowsPure = (tags: TableItem['tag'][]): GridRow[] => [
	{ tag: HEADER },
	{ tag: SUBHEADER },
	...tags.map((itemTag, index) => ({
		tag: TABLE_ITEM,
		payload: {
			itemIndex: index,
			itemTag,
		},
	})),
];

export const getGridRows = createSelector([getTableItemTags], getGridRowsPure);

export const getGridColumnsPure = (
	viewMode: ViewMode,
	{ columns: columnStates }: FieldColumnsState,
	customFields: CustomField[],
	atlasConnectInstalled: boolean,
	isConfluenceMacroProxyMode: boolean,
	focusConnectedWithReadPermissions: boolean,
	isReleasesFeatureEnabled: boolean,
): GridColumn[] => {
	// Custom field columns could be added and then removed from a plan, so we filter the available columns
	const columns = columnStates
		.filter((column) => {
			if (column.id === 'goal' && !atlasConnectInstalled) {
				return false;
			}

			if (column.id === 'release' && !isReleasesFeatureEnabled) {
				return false;
			}

			return (
				(isColumnId(column.id, isConfluenceMacroProxyMode, focusConnectedWithReadPermissions) ||
					customFields.some((field) => `${field.id}` === column.id)) &&
				column.isVisible
			);
		})
		.map((column) => ({ tag: FIELD, payload: column }));

	return [
		{ tag: SCOPE },
		...columns,
		...(viewMode === VIEW_MODES.LIST ? [{ tag: ADD_FIELDS }] : []),
		...(viewMode === VIEW_MODES.TIMELINE && columns.length === 0 ? [{ tag: EMPTY }] : []),
		...(viewMode === VIEW_MODES.TIMELINE ? [{ tag: TIMELINE }] : []),
	];
};

export const getGridColumns = createSelector(
	[
		getViewMode,
		getFieldColumnsViewSettingsByViewMode,
		getSupportedCustomFields,
		isAtlasConnectInstalled,
		isConfluenceMacroProxyForIdeas,
		isFocusConnectedWithReadPermissions,
		isReleasesEnabled,
	],
	getGridColumnsPure,
);

export const getRowHeightsPure = (tableItems: TableItem[]) =>
	TIMELINE_HEADERS_ROWS.map(({ height }) => height).concat(
		tableItems.map(
			({
				height /* JPO-28647 - This could be an integer larger than 1 when it's a row with overlapping sprints */,
			}) => (height ?? 1) * 40,
		),
	);

export const getRowHeights = createSelector([getTableItems], getRowHeightsPure);

/**
 * Returns the stickiness of rows
 *
 * This allows better performance the the usage at getColumnConfigs()
 */
export const getRowStickinessPure = (gridRows: GridRow[]) =>
	gridRows.map(
		(row) => row.tag === HEADER || row.tag === SUBHEADER || row.payload.itemTag === TABLE_GROUP,
	);
export const getRowStickiness = createSelector([getGridRows], getRowStickinessPure);

export const getRowConfigsPure = (
	rowsStickiness: boolean[],
	rowHeights: number[],
	// make rowLevels, rowExpandable, rowExpanded required when treegrid_keyboard_expand is cleaned up
	rowLevel?: RowConfig['level'][],
	rowExpandable?: boolean[],
	rowExpanded?: boolean[],
	// make rowIds required when treegrid_keyboard_navigation is cleaned up
	rowIds?: string[],
) => {
	let currentTop = 0;

	return rowsStickiness.map((sticky, index): RowConfig => {
		const height = rowHeights[index];
		const top = currentTop;
		currentTop += height;
		const expandable = rowExpandable?.[index];
		return {
			...(rowIds ? { id: rowIds[index] } : {}),
			height,
			top,
			thead: [0, 1].includes(index),
			sticky,
			...(rowLevel ? { level: rowLevel[index] } : {}),
			...(expandable ? { expandable } : {}),
			...(rowExpanded && expandable ? { expanded: rowExpanded[index] } : {}),
		};
	});
};

export const getRowExpandablePure = (tableItems: TableItem[]): boolean[] => [
	...TIMELINE_HEADERS_ROWS.map(() => false),
	...tableItems.map((item) => {
		switch (item?.tag) {
			case TABLE_GROUP:
				return true;
			case TABLE_GROUP_HEADER:
				return true;
			case TABLE_ISSUE:
				return !!item.value.isExpandable;
			default:
				return false;
		}
	}),
];

export const getRowExpandedPure = (tableItems: TableItem[]): boolean[] => [
	...TIMELINE_HEADERS_ROWS.map(() => false),
	...tableItems.map((item) => {
		switch (item?.tag) {
			case TABLE_GROUP:
				return item.isExpanded;
			case TABLE_GROUP_HEADER:
				return !!item.value.isExpanded;
			case TABLE_ISSUE:
				return !!item.value.isExpanded;
			default:
				return false;
		}
	}),
];

export const getRowExpanded = createSelector([getTableItems], getRowExpandedPure);
export const getRowExpandable = createSelector([getTableItems], getRowExpandablePure);
/**
 * Returns the hierarchy configuration for table items
 *
 * Hierarchy levels:
 * - 0: Header rows
 * - 1: Group rows
 * - 2: Group header rows
 * - 3+: Issue rows with depth
 */
export const getRowLevelsPure = (tableItems: TableItem[]): RowConfig['level'][] => {
	const items = tableItems.map((item) => {
		switch (item?.tag) {
			case TABLE_GROUP:
				return 1;
			case TABLE_GROUP_HEADER:
				return 2;
			case TABLE_BLANK_ROW:
				return 2;
			case TABLE_ISSUE:
			case TABLE_INLINE_CREATE:
				return 3 + item.value.depth;
			default:
				return 1;
		}
	});

	return TIMELINE_HEADERS_ROWS.map(({ level }) => level).concat(items);
};

const getRowLevel = createSelector([getTableItems], getRowLevelsPure);

export const getColumnConfigsPure = (viewMode: ViewMode, gridColumns: GridColumn[]) =>
	gridColumns.map((column): ColumnConfig => {
		if (column.tag === SCOPE) {
			return { id: 'scope', sticky: true };
		}

		if (column.tag === TIMELINE) {
			return { id: 'timeline', sticky: false };
		}

		if (column.tag === EMPTY) {
			return { id: 'empty', sticky: true };
		}

		if (column.tag === ADD_FIELDS && viewMode === VIEW_MODES.TIMELINE) {
			return { id: 'addFields', sticky: true };
		}

		if (column.tag === ADD_FIELDS) {
			return { id: 'addFields', sticky: false };
		}

		return { id: column.payload.id, sticky: viewMode === VIEW_MODES.TIMELINE };
	});

export const getColumnConfigs = createSelector([getViewMode, getGridColumns], getColumnConfigsPure);

export const getColumnCount = createSelector([getGridColumns], (gridColumns) => gridColumns.length);

export const getScrollIntoViewIndexPure = (index: number | undefined) =>
	index === undefined ? undefined : index + 2; /* header and sub-header */

export const getScrollIntoViewIndex = createSelector(
	[getScrollToIndex],
	getScrollIntoViewIndexPure,
);

export const getRowConfigs = functionWithCondition(
	() => fg('treegrid_keyboard_navigation'), // Including treegrid_keyboard_expand
	createSelector(
		[getRowStickiness, getRowHeights, getRowLevel, getRowExpandable, getRowExpanded, getGridRowIds],
		getRowConfigsPure,
	),
	functionWithCondition(
		() => fg('treegrid_keyboard_expand'),
		createSelector(
			[getRowStickiness, getRowHeights, getRowLevel, getRowExpandable, getRowExpanded],
			getRowConfigsPure,
		),
		createSelector([getRowStickiness, getRowHeights], getRowConfigsPure),
	),
);
